import React, { useMemo } from 'react';
import { Button } from 'antd';
import {
	DndContext,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	arrayMove,
	rectSortingStrategy,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { CHECK, DRAG_HANDLE } from '../icons';

/**
 * `ids` with `id` moved by `delta` places (clamped), or the same array when
 * the move goes nowhere — the Move up / Move down menu entries every
 * sortable surface also offers, for a pointer that cannot drag and a screen
 * reader that would rather not.
 */
export function moveId(ids, id, delta) {
	const from = ids.indexOf(id);
	if (from < 0) return ids;
	const to = Math.max(0, Math.min(ids.length - 1, from + delta));
	if (to === from) return ids;
	return arrayMove(ids, from, to);
}

function SortableRow({ id, label, index, render, className, disabled }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id, disabled });
	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};
	// The handle is the ONLY thing that starts a drag: the row itself stays a
	// link / button that opens or launches on click and on Enter. Space or
	// Enter on the handle picks the row up, the arrows move it, Space drops,
	// Escape puts it back (dnd-kit's keyboard sensor).
	const handle = disabled ? null : (
		<button
			type="button"
			ref={setActivatorNodeRef}
			className="sortable-handle"
			aria-label={`Reorder ${label}`}
			title="Drag to reorder"
			{...attributes}
			{...listeners}
		>
			<DRAG_HANDLE aria-hidden="true" />
		</button>
	);
	return (
		<li
			ref={setNodeRef}
			style={style}
			className={['sortable-list__item', isDragging ? 'sortable-list__item--dragging' : '', className].filter(Boolean).join(' ')}
			data-sortable-id={id}
		>
			{render({ handle, isDragging, index })}
		</li>
	);
}

/**
 * The door into a list's reorder mode, and out of it: **Reorder** (a grip
 * glyph) until pressed, then **Done** (a check). Reordering is deliberate —
 * low velocity — so a sortable list draws no grips until the person asks for
 * them, and a stray drag on a phone can never move a pin. It sits in the
 * list's GroupHeader `action` slot. `what` names the list for a screen
 * reader ("Reorder pinned launchers" / "Done reordering pinned launchers").
 */
export function ReorderButton({ active = false, onClick, what, className = '' }) {
	return (
		<Button
			size="small"
			type="text"
			className={['reorder-button', active ? 'reorder-button--active' : '', className].filter(Boolean).join(' ')}
			icon={active ? <CHECK aria-hidden="true" /> : <DRAG_HANDLE aria-hidden="true" />}
			aria-label={what ? `${active ? 'Done reordering' : 'Reorder'} ${what}` : undefined}
			onClick={onClick}
		>
			{active ? 'Done' : 'Reorder'}
		</Button>
	);
}

/**
 * THE drag-to-reorder list: dnd-kit with a pointer sensor and a keyboard
 * sensor, a grip handle on every movable row, and screen-reader
 * announcements that say the row's own name. Used by the Home page's
 * sections and the collections page's Favorites.
 *
 *   <SortableList
 *     items={pins}                       // each with an `id`
 *     getLabel={pin => pin.title}        // what the handle and announcer say
 *     onReorder={(ids) => save(ids)}     // the new order of ALL ids
 *     renderItem={(pin, { handle }) => <Row pin={pin} handle={handle} />}
 *     layout="grid"                      // 'list' (default) | 'grid'
 *     reordering={mode}                  // grips only while true (ReorderButton)
 *   />
 *
 * The caller owns the order and saves it (optimistically, with rollback);
 * this component never keeps its own copy. `fixed` ids render without a
 * handle and never move — the personal collection. `itemClassName` is a
 * class for every row, or a function of the item for a class of its own (a
 * dashboard tile's width and height). `reordering={false}`
 * renders the same list with no grip on any row and nothing draggable: the
 * resting state of every sortable surface, which a ReorderButton flips.
 */
export default function SortableList({
	items = [],
	getLabel = item => String(item?.id ?? ''),
	onReorder,
	renderItem,
	layout = 'list',
	fixed = [],
	className = '',
	itemClassName = '',
	ariaLabel,
	reordering = true,
}) {
	const sensors = useSensors(
		// A few pixels of travel before a drag starts, so a tap on the handle
		// on a phone is not a drag.
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);
	const ids = useMemo(() => items.map(item => item.id), [items]);
	const fixedSet = useMemo(() => new Set(fixed), [fixed]);
	const labelOf = useMemo(() => {
		const map = new Map(items.map(item => [item.id, getLabel(item)]));
		return id => map.get(id) || 'item';
	}, [items, getLabel]);

	const handleDragEnd = ({ active, over }) => {
		if (!reordering) return;
		if (!over || active.id === over.id) return;
		if (fixedSet.has(over.id)) return;
		const from = ids.indexOf(active.id);
		const to = ids.indexOf(over.id);
		if (from < 0 || to < 0) return;
		if (typeof onReorder === 'function') onReorder(arrayMove(ids, from, to), active.id);
	};

	const announcements = useMemo(() => ({
		onDragStart: ({ active }) => `Picked up ${labelOf(active.id)}. Use the arrow keys to move it, space to drop it, escape to cancel.`,
		onDragOver: ({ active, over }) => (over
			? `${labelOf(active.id)} is now at position ${ids.indexOf(over.id) + 1} of ${ids.length}.`
			: `${labelOf(active.id)} is no longer over the list.`),
		onDragEnd: ({ active, over }) => (over
			? `Dropped ${labelOf(active.id)} at position ${ids.indexOf(over.id) + 1} of ${ids.length}.`
			: `Dropped ${labelOf(active.id)}.`),
		onDragCancel: ({ active }) => `Moving ${labelOf(active.id)} was cancelled.`,
	}), [ids, labelOf]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
			accessibility={{
				announcements,
				screenReaderInstructions: {
					draggable: 'To reorder, press space or enter on the handle, move with the arrow keys, then press space again to drop, or escape to cancel.',
				},
			}}
		>
			<SortableContext items={ids} strategy={layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}>
				<ul
					className={['sortable-list', `sortable-list--${layout}`, reordering ? 'sortable-list--reordering' : '', className].filter(Boolean).join(' ')}
					aria-label={ariaLabel}
				>
					{items.map((item, index) => (
						<SortableRow
							key={item.id}
							id={item.id}
							label={labelOf(item.id)}
							index={index}
							disabled={!reordering || fixedSet.has(item.id)}
							className={typeof itemClassName === 'function' ? itemClassName(item) : itemClassName}
							render={props => renderItem(item, props)}
						/>
					))}
				</ul>
			</SortableContext>
		</DndContext>
	);
}
